import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import KPIGrid from '@/components/admin/KPIGrid';
import MetricCard from '@/components/admin/MetricCard';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Activity, AlertTriangle, MessageSquare, BrainCircuit, Bug, TestTube, ArrowRight, BarChart2, Inbox } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';

function percentile(values: number[], p: number) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.floor((p / 100) * (sorted.length - 1));
  return sorted[idx];
}

export default function AdminHealthDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const { data: kpis, isLoading } = useQuery({
    queryKey: ['admin-health-kpis'],
    queryFn: async () => {
      // Time windows
      const since24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const since1h = new Date(Date.now() - 60 * 60 * 1000).toISOString();

      // Recent assistant messages (read minimal fields)
      const { data: recentAssist } = await supabase
        .from('chat_messages')
        .select('id, timing_metadata, content, created_at')
        .eq('role', 'assistant')
        .gte('created_at', since24h)
        .order('created_at', { ascending: false })
        .limit(2000);

      const assistantMsgs24h = recentAssist?.length || 0;

      // Primary latency from timing_metadata when present
      const latenciesFromMeta = (recentAssist || [])
        .map((m: any) => Number(m?.timing_metadata?.total_latency_ms || 0))
        .filter((n) => Number.isFinite(n) && n > 0);

      let p50 = percentile(latenciesFromMeta, 50);
      let p95 = percentile(latenciesFromMeta, 95);
      const msgsWithTiming24h = latenciesFromMeta.length;

      // Fallback latency: estimate from user→assistant time deltas in the last 24h
      if (!msgsWithTiming24h) {
        const { data: lastMsgs } = await supabase
          .from('chat_messages')
          .select('id, chat_history_id, role, created_at')
          .in('role', ['user', 'assistant'] as any)
          .gte('created_at', since24h)
          .not('chat_history_id', 'is', null)
          .order('created_at', { ascending: true })
          .limit(2000);

        const latenciesEst: number[] = [];
        const byHistory = new Map<string, any[]>();
        (lastMsgs || []).forEach((m: any) => {
          const key = m.chat_history_id as string;
          if (!key) return;
          if (!byHistory.has(key)) byHistory.set(key, []);
          byHistory.get(key)!.push(m);
        });
        for (const list of byHistory.values()) {
          for (let i = 0; i < list.length; i++) {
            const m = list[i];
            if (m.role === 'user') {
              const next = list.slice(i + 1).find((x) => x.role === 'assistant');
              if (next) {
                const dt = new Date(next.created_at).getTime() - new Date(m.created_at).getTime();
                if (Number.isFinite(dt) && dt > 0) latenciesEst.push(dt);
              }
            }
          }
        }
        p50 = percentile(latenciesEst, 50);
        p95 = percentile(latenciesEst, 95);
      }

      // Vector accuracy proxy (avg accuracy_score from timing_metadata when present)
      const accuracyScores = (recentAssist || [])
        .map((m: any) => Number(m?.timing_metadata?.accuracy_score))
        .filter((n) => Number.isFinite(n));
      const avgAccuracy = accuracyScores.length
        ? accuracyScores.reduce((a, b) => a + b, 0) / accuracyScores.length
        : 0;

      // Active conversations: distinct chat_history_id seen in the last hour
      const { data: recentMsgsHour } = await supabase
        .from('chat_messages')
        .select('chat_history_id')
        .gte('created_at', since1h)
        .not('chat_history_id', 'is', null)
        .limit(2000);
      const activeConvos = Array.from(new Set((recentMsgsHour || []).map((m: any) => m.chat_history_id))).length;

      // Knowledge gaps: rely on posit_task_flags opened last 24h
      const { count: gaps24h } = await supabase
        .from('posit_task_flags')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', since24h);

      // Error logs (vector operations failures in last 24h)
      const { count: errorLogs24h } = await supabase
        .from('vector_operations_log')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'failed')
        .gte('created_at', since24h);

      // Vector queue backlog
      const { count: vectorPending } = await supabase
        .from('vector_sync_queue')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      return {
        p50, p95,
        avgAccuracy,
        activeConvos: activeConvos ?? 0,
        gaps24h: gaps24h ?? 0,
        errorLogs24h: errorLogs24h ?? 0,
        assistantMsgs24h,
        msgsWithTiming24h,
        vectorPending: vectorPending ?? 0,
      };
    },
    enabled: !!user,
  });

  // Inline recent error logs for unified health view
  const { data: errorLogs = [], isLoading: isLoadingErrors } = useQuery({
    queryKey: ['admin-health-error-logs-inline'],
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('vector_operations_log')
        .select('id, created_at, status, operation_type, posit_id, pinecone_namespace, function_name, error_message')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(10);
      return data || [];
    },
    enabled: !!user,
  });

  // Conversations explorer: list recent conversations and inspect message pairs
  const { data: recentConversations = [] } = useQuery({
    queryKey: ['admin-health-recent-conversations'],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: rows } = await supabase
        .from('chat_messages')
        .select('chat_history_id, posit_id, role, created_at')
        .not('chat_history_id', 'is', null)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(1500);
      const groups = new Map<string, { chat_history_id: string; posit_id: string | null; last_at: string; count: number }>();
      (rows || []).forEach((r: any) => {
        const id = r.chat_history_id as string;
        if (!groups.has(id)) {
          groups.set(id, { chat_history_id: id, posit_id: r.posit_id || null, last_at: r.created_at, count: 1 });
        } else {
          const g = groups.get(id)!;
          g.count += 1;
          if (new Date(r.created_at).getTime() > new Date(g.last_at).getTime()) g.last_at = r.created_at;
        }
      });
      // Fetch posit titles
      const positIds = Array.from(new Set(Array.from(groups.values()).map((g) => g.posit_id).filter(Boolean))) as string[];
      let positMap: Record<string, string> = {};
      if (positIds.length) {
        const { data: posits } = await supabase
          .from('posits')
          .select('id, title')
          .in('id', positIds);
        (posits || []).forEach((p: any) => { positMap[p.id] = p.title; });
      }
      return Array.from(groups.values()).slice(0, 200).map(g => ({
        ...g,
        posit_title: g.posit_id ? (positMap[g.posit_id] || 'Untitled Posit') : '—'
      }));
    },
    enabled: !!user,
  });

  const { data: conversationMessages = [] } = useQuery({
    queryKey: ['admin-health-conversation-messages', selectedConversationId],
    queryFn: async () => {
      if (!selectedConversationId) return [];
      const { data } = await supabase
        .from('chat_messages')
        .select('id, role, content, created_at, timing_metadata')
        .eq('chat_history_id', selectedConversationId)
        .order('created_at', { ascending: true })
        .limit(400);
      return data || [];
    },
    enabled: !!user && !!selectedConversationId,
  });

  const messagePairs = useMemo(() => {
    const pairs: { user?: any; assistant?: any }[] = [];
    if (!conversationMessages?.length) return pairs;
    let pendingUser: any | null = null;
    for (const m of conversationMessages) {
      if (m.role === 'user') {
        if (pendingUser) pairs.push({ user: pendingUser });
        pendingUser = m;
      } else {
        if (pendingUser) {
          pairs.push({ user: pendingUser, assistant: m });
          pendingUser = null;
        } else {
          pairs.push({ assistant: m });
        }
      }
    }
    if (pendingUser) pairs.push({ user: pendingUser });
    return pairs;
  }, [conversationMessages]);

  return (
    <DashboardLayout title="Health Dashboard" description="Operational KPIs with drill‑downs" maxWidth="7xl">
      <KPIGrid>
        <MetricCard title="Avg Response Time (p50)" value={isLoading ? '…' : `${Math.round(kpis?.p50 || 0)} ms`} icon={<Activity className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/health/conversations')} />
        <MetricCard title="Slow Response (p95)" value={isLoading ? '…' : `${Math.round(kpis?.p95 || 0)} ms`} icon={<Activity className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/health/conversations')} />
        <MetricCard title="Active Conversations (1h)" value={isLoading ? '…' : kpis?.activeConvos} icon={<MessageSquare className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/health/conversations')} />
        <MetricCard title="Knowledge Gaps (24h)" value={isLoading ? '…' : kpis?.gaps24h} icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/health/knowledge-gaps')} />
        <MetricCard title="Vector Accuracy (proxy)" value={isLoading ? '…' : `${Math.round((kpis?.avgAccuracy || 0))}` } icon={<BrainCircuit className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/health/vector-accuracy')} />
        <MetricCard title="Error Logs (24h)" value={isLoading ? '…' : kpis?.errorLogs24h} icon={<Bug className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/health')} />
        <MetricCard title="Assistant Msgs (24h)" value={isLoading ? '…' : kpis?.assistantMsgs24h} icon={<BarChart2 className="h-4 w-4 text-muted-foreground" />} />
        <MetricCard title="Msgs With Timing (24h)" value={isLoading ? '…' : kpis?.msgsWithTiming24h} icon={<Activity className="h-4 w-4 text-muted-foreground" />} />
        <MetricCard title="Vector Queue (pending)" value={isLoading ? '…' : kpis?.vectorPending} icon={<Inbox className="h-4 w-4 text-muted-foreground" />} />
      </KPIGrid>

      {/* Unified tools and inline logs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Posit Performance Test quick entry */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <TestTube className="h-4 w-4" /> Posit Performance Test
            </CardTitle>
            <Button size="sm" onClick={() => navigate('/admin/health/batch-testing')}>
              Open Tester <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">Run isolated batch conversations across posits with groundedness scoring. Tests do not write to main chat.</p>
            <div className="text-xs text-muted-foreground">Also available under Admin → Health → Posit Performance Test.</div>
          </CardContent>
        </Card>

        {/* Inline recent error logs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Bug className="h-4 w-4" /> Recent Error Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Op</TableHead>
                    <TableHead>Posit</TableHead>
                    <TableHead>Function</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!isLoadingErrors && errorLogs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">No recent errors</TableCell>
                    </TableRow>
                  )}
                  {errorLogs.map((row: any) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</TableCell>
                      <TableCell className="capitalize">{row.status}</TableCell>
                      <TableCell>{row.operation_type}</TableCell>
                      <TableCell className="font-mono text-xs">{row.posit_id || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{row.function_name || '-'}</TableCell>
                      <TableCell className="text-xs max-w-[280px] truncate" title={row.error_message || ''}>{row.error_message || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversations inspector */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Recent Conversations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Last Activity</TableHead>
                    <TableHead>Posit</TableHead>
                    <TableHead className="text-right">Msgs</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentConversations.length === 0 && (
                    <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">No recent conversations</TableCell></TableRow>
                  )}
                  {recentConversations.map((c: any) => (
                    <TableRow key={c.chat_history_id} className={`cursor-pointer hover:bg-muted/50 ${selectedConversationId === c.chat_history_id ? 'bg-muted/70' : ''}`} onClick={() => setSelectedConversationId(c.chat_history_id)}>
                      <TableCell className="whitespace-nowrap">{new Date(c.last_at).toLocaleString()}</TableCell>
                      <TableCell className="truncate max-w-[180px]" title={c.posit_title}>{c.posit_title}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{c.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Conversation Messages</CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedConversationId ? (
              <div className="text-sm text-muted-foreground">Select a conversation to view message/response pairs.</div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[48%]">User Message</TableHead>
                      <TableHead className="w-[48%]">Assistant Response</TableHead>
                      <TableHead className="w-[4%] text-right">ms</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {messagePairs.length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">No messages in this conversation</TableCell></TableRow>
                    )}
                    {messagePairs.map((p, idx) => {
                      const dt = p.user && p.assistant
                        ? (new Date(p.assistant.created_at).getTime() - new Date(p.user.created_at).getTime())
                        : undefined;
                      return (
                        <TableRow key={idx}>
                          <TableCell className="align-top">
                            <div className="text-xs text-muted-foreground mb-1">{p.user ? new Date(p.user.created_at).toLocaleString() : '-'}</div>
                            <div className="text-sm whitespace-pre-wrap break-words">{p.user?.content || <span className="text-muted-foreground">—</span>}</div>
                          </TableCell>
                          <TableCell className="align-top">
                            <div className="text-xs text-muted-foreground mb-1">{p.assistant ? new Date(p.assistant.created_at).toLocaleString() : '-'}</div>
                            <div className="text-sm whitespace-pre-wrap break-words">{p.assistant?.content || <span className="text-muted-foreground">—</span>}</div>
                          </TableCell>
                          <TableCell className="text-right align-top font-mono text-xs">{Number.isFinite(dt as number) ? Math.max(0, dt as number) : (p.assistant?.timing_metadata?.total_latency_ms ?? '—')}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

